import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Search, Trash2, Download, Video, X, ImageIcon, Loader2, RefreshCw,
  Maximize2, Star, CheckSquare, Square, FileArchive, Tag, Plus, Dices,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import LeftRail from "@/components/LeftRail";
import {
  fetchHistory, deleteGeneration, upscaleImage, updateGeneration, fetchTags, exportHistory, mediaDownloadUrl,
} from "@/lib/falApi";

const cls = (...a) => a.filter(Boolean).join(" ");

export default function Gallery() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const urlTag = sp.get("tag") || "";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");
  const [favOnly, setFavOnly] = useState(false);
  const [tagFilter, setTagFilter] = useState(urlTag);
  const [allTags, setAllTags] = useState([]);
  const [active, setActive] = useState(null);
  const [upscaling, setUpscaling] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const [tagInput, setTagInput] = useState("");

  const load = useCallback(async (t = type, query = q, fav = favOnly, tg = tagFilter) => {
    setLoading(true);
    try { setItems(await fetchHistory(t, query, { favorite: fav, tag: tg })); } catch (e) { console.error("Failed to load history", e); }
    setLoading(false);
  }, [type, q, favOnly, tagFilter]);

  const loadTags = useCallback(async () => { try { setAllTags(await fetchTags()); } catch (e) { console.error("Failed to load tags", e); } }, []);

  useEffect(() => { load("all", "", false, urlTag); loadTags(); }, []); // eslint-disable-line
  useEffect(() => {
    const id = setTimeout(() => load(type, q, favOnly, tagFilter), 300);
    return () => clearTimeout(id);
  }, [q, type, favOnly, tagFilter]); // eslint-disable-line

  const patchLocal = (rec) => {
    setItems((p) => p.map((x) => (x.id === rec.id ? rec : x)));
    if (active?.id === rec.id) setActive(rec);
  };

  const toggleFav = async (h, e) => {
    e?.stopPropagation();
    try { patchLocal(await updateGeneration(h.id, { favorite: !h.favorite })); }
    catch (err) { toast.error("Could not update"); }
  };

  const addTag = async (h, tag) => {
    const t = tag.trim();
    if (!t) return;
    const tags = Array.from(new Set([...(h.tags || []), t]));
    try { patchLocal(await updateGeneration(h.id, { tags })); setTagInput(""); loadTags(); }
    catch (err) { toast.error("Could not add tag"); }
  };
  const removeTag = async (h, tag) => {
    const tags = (h.tags || []).filter((x) => x !== tag);
    try { patchLocal(await updateGeneration(h.id, { tags })); loadTags(); }
    catch (err) { toast.error("Could not remove tag"); }
  };

  const remove = async (id) => {
    await deleteGeneration(id).catch(() => {});
    setItems((p) => p.filter((x) => x.id !== id));
    if (active?.id === id) setActive(null);
    toast.success("Removed");
  };

  const remix = (h) => {
    navigate("/", { state: { remix: { model_id: h.model_id, prompt: h.prompt, params: h.params, type: h.type } } });
  };

  const doUpscale = async (h) => {
    setUpscaling(true);
    try {
      const rec = await upscaleImage(h.outputs?.[0]?.url || h.thumbnail, h.id);
      toast.success("Upscaled ×2 — saved to gallery");
      setActive(rec);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upscale failed");
    } finally { setUpscaling(false); }
  };

  const toggleSelect = (id) => {
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const exportZip = async () => {
    if (!selected.size) { toast.error("Select renders first"); return; }
    setExporting(true);
    try {
      const blob = await exportHistory([...selected]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "fal-studio-export.zip"; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${selected.size} render${selected.size > 1 ? "s" : ""}`);
    } catch (e) { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  const onCardClick = (h) => { if (selectMode) toggleSelect(h.id); else setActive(h); };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex" data-testid="gallery-page">
      <LeftRail />
      <div className="flex-1 min-w-0">
        <header className="h-14 border-b border-zinc-800 flex items-center justify-between px-4 sm:px-6 sticky top-0 bg-[#0a0a0a] z-10">
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="gallery-back">
              <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white"><ArrowLeft className="h-4 w-4 mr-1.5" /> Studio</Button>
            </Link>
            <h1 className="font-display font-bold text-lg tracking-tight">Gallery</h1>
            <span className="font-mono text-[11px] text-zinc-500 hidden sm:inline">{items.length} renders</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setFavOnly((v) => !v)} data-testid="gallery-fav-filter"
              className={cls("flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-mono transition-colors border",
                favOnly ? "border-[#faff69] text-[#faff69] bg-[#faff69]/10" : "border-zinc-800 text-zinc-500 hover:text-white")}>
              <Star className={cls("h-3.5 w-3.5", favOnly && "fill-[#faff69]")} /> favorites
            </button>
            <div className="flex gap-1">
              {["all", "image", "video"].map((f) => (
                <button key={f} onClick={() => setType(f)} data-testid={`gallery-filter-${f}`}
                  className={cls("px-2.5 py-1 rounded text-[11px] font-mono transition-colors",
                    type === f ? "bg-[#faff69] text-black" : "text-zinc-500 hover:text-white")}>{f}</button>
              ))}
            </div>
            <div className="relative hidden sm:block">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input data-testid="gallery-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search prompts…"
                className="h-8 w-40 sm:w-56 pl-8 bg-[#121212] border-zinc-800 focus-visible:ring-[#faff69] text-sm" />
            </div>
            <Button size="sm" variant={selectMode ? "default" : "outline"} data-testid="gallery-select-toggle"
              onClick={() => { setSelectMode((v) => !v); setSelected(new Set()); }}
              className={cls(selectMode ? "bg-[#faff69] text-black hover:bg-[#e6eb52]" : "border-zinc-800 text-zinc-300 hover:text-white")}>
              <CheckSquare className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{selectMode ? "Done" : "Select"}</span>
            </Button>
          </div>
        </header>

        {/* tag filter row */}
        {allTags.length > 0 && (
          <div className="flex items-center gap-2 px-4 sm:px-6 py-2.5 border-b border-zinc-800 overflow-x-auto" data-testid="tag-filter-row">
            <Tag className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
            <button onClick={() => setTagFilter("")} data-testid="tag-filter-all"
              className={cls("shrink-0 px-2.5 py-1 rounded-full text-[11px] font-mono border transition-colors",
                tagFilter === "" ? "border-[#faff69] text-[#faff69] bg-[#faff69]/10" : "border-zinc-800 text-zinc-500 hover:text-white")}>all</button>
            {allTags.map((t) => (
              <button key={t} onClick={() => setTagFilter(t)} data-testid={`tag-filter-${t}`}
                className={cls("shrink-0 px-2.5 py-1 rounded-full text-[11px] font-mono border transition-colors",
                  tagFilter === t ? "border-[#faff69] text-[#faff69] bg-[#faff69]/10" : "border-zinc-800 text-zinc-400 hover:text-white")}>#{t}</button>
            ))}
          </div>
        )}

        {/* bulk selection toolbar */}
        {selectMode && (
          <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-2.5 border-b border-zinc-800 bg-[#121212]" data-testid="bulk-toolbar">
            <span className="font-mono text-[11px] text-zinc-400" data-testid="bulk-count">{selected.size} selected</span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set(items.map((i) => i.id)))} className="text-zinc-400 hover:text-white text-xs" data-testid="bulk-select-all">Select all</Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="text-zinc-400 hover:text-white text-xs" data-testid="bulk-clear">Clear</Button>
              <Button size="sm" onClick={exportZip} disabled={exporting || !selected.size} data-testid="bulk-export"
                className="bg-[#faff69] text-black hover:bg-[#e6eb52] font-semibold">
                {exporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileArchive className="h-4 w-4 mr-1.5" />} Export ZIP
              </Button>
            </div>
          </div>
        )}

        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 text-[#faff69] animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-24 text-zinc-600">
              <ImageIcon className="h-10 w-10 mx-auto" />
              <p className="mt-3 text-sm">{q || favOnly || tagFilter ? "No renders match your filters." : "No renders yet."}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {items.map((h) => {
                const sel = selected.has(h.id);
                return (
                  <div key={h.id} role="button" tabIndex={0} onClick={() => onCardClick(h)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onCardClick(h); } }}
                    data-testid={`gallery-item-${h.id}`}
                    className={cls("relative group rounded-lg overflow-hidden border aspect-square transition-colors cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-[#faff69]",
                      selectMode && sel ? "border-[#faff69] ring-2 ring-[#faff69]" : "border-zinc-800 hover:border-[#faff69]")}>
                    {h.type === "video"
                      ? <video src={h.thumbnail} className="w-full h-full object-cover" muted />
                      : <img src={h.thumbnail} alt={h.prompt} className="w-full h-full object-cover" />}

                    {/* select checkbox */}
                    {selectMode && (
                      <div className="absolute top-2 left-2 z-10" data-testid={`select-${h.id}`}>
                        {sel ? <CheckSquare className="h-5 w-5 text-[#faff69] fill-black" /> : <Square className="h-5 w-5 text-white/80" />}
                      </div>
                    )}

                    {/* favorite star */}
                    <button onClick={(e) => toggleFav(h, e)} data-testid={`fav-${h.id}`}
                      className={cls("absolute top-2 right-2 z-10 p-1 rounded-md bg-black/50 backdrop-blur-sm transition-opacity",
                        h.favorite ? "opacity-100" : "opacity-0 group-hover:opacity-100")}>
                      <Star className={cls("h-4 w-4", h.favorite ? "fill-[#faff69] text-[#faff69]" : "text-white")} />
                    </button>

                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3 text-left pointer-events-none">
                      <span className="text-[10px] font-mono text-[#faff69]">{h.model_name}</span>
                      <p className="text-[11px] text-zinc-200 line-clamp-2 mt-0.5">{h.prompt || "image input"}</p>
                      {h.tags?.length > 0 && <div className="flex flex-wrap gap-1 mt-1">{h.tags.slice(0, 3).map((t) => <span key={t} className="text-[9px] font-mono text-zinc-400">#{t}</span>)}</div>}
                    </div>
                    {h.type === "video" && <Video className="absolute top-2 left-1/2 -translate-x-1/2 h-4 w-4 text-white/80" />}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
          <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-3xl p-0 overflow-hidden" data-testid="gallery-preview">
            <DialogTitle className="sr-only">Render preview</DialogTitle>
            {active && (
              <div className="grid md:grid-cols-[1fr_280px]">
                <div className="bg-black grid place-items-center max-h-[70vh] overflow-hidden">
                  {active.type === "video"
                    ? <video src={active.outputs?.[0]?.url || active.thumbnail} controls className="w-full max-h-[70vh]" />
                    : <img src={active.outputs?.[0]?.url || active.thumbnail} alt={active.prompt} className="w-full max-h-[70vh] object-contain" />}
                </div>
                <div className="p-5 flex flex-col gap-3 border-t md:border-t-0 md:border-l border-zinc-800 max-h-[70vh] overflow-y-auto">
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-[11px] text-[#faff69]">{active.model_name}</div>
                    <button onClick={(e) => toggleFav(active, e)} data-testid="preview-fav" className="p-1 rounded hover:bg-zinc-800">
                      <Star className={cls("h-4 w-4", active.favorite ? "fill-[#faff69] text-[#faff69]" : "text-zinc-400")} />
                    </button>
                  </div>
                  <p className="text-sm text-zinc-200 leading-relaxed max-h-32 overflow-y-auto">{active.prompt || "image input"}</p>
                  {active.seed != null && (
                    <div className="font-mono text-[11px] text-zinc-500 flex items-center gap-1.5"><Dices className="h-3 w-3 text-[#faff69]" /> seed {active.seed}</div>
                  )}
                  <div className="font-mono text-[11px] text-zinc-500">{new Date(active.created_at).toLocaleString()}</div>

                  {/* tags editor */}
                  <div>
                    <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 mb-1.5"><Tag className="h-3 w-3" /> tags / collection</div>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {(active.tags || []).map((t) => (
                        <span key={t} data-testid={`preview-tag-${t}`} className="flex items-center gap-1 text-[11px] font-mono bg-zinc-800 rounded-full pl-2.5 pr-1 py-0.5">
                          #{t}<button onClick={() => removeTag(active, t)} data-testid={`preview-tag-remove-${t}`} className="hover:text-[#FF3B30]"><X className="h-3 w-3" /></button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input data-testid="preview-tag-input" value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(active, tagInput); } }}
                        placeholder="add a tag…" className="h-8 bg-[#0a0a0a] border-zinc-800 focus-visible:ring-[#faff69] text-sm font-mono" />
                      <Button size="sm" variant="outline" onClick={() => addTag(active, tagInput)} data-testid="preview-tag-add" className="border-zinc-800 text-zinc-300 shrink-0"><Plus className="h-4 w-4" /></Button>
                    </div>
                  </div>

                  <div className="mt-auto space-y-2 pt-2">
                    <div className="flex gap-2">
                      <Button onClick={() => remix(active)} data-testid="gallery-remix" className="flex-1 bg-[#faff69] hover:bg-[#e6eb52] text-black">
                        <RefreshCw className="h-4 w-4 mr-1.5" /> Remix
                      </Button>
                      {active.type === "image" && (
                        <Button onClick={() => doUpscale(active)} disabled={upscaling} variant="outline"
                          className="border-zinc-700 text-white hover:bg-zinc-800" data-testid="gallery-upscale">
                          {upscaling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Maximize2 className="h-4 w-4" />}
                        </Button>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <a href={mediaDownloadUrl(active.outputs?.[0]?.url, `fal-studio-${active.type || "image"}`)} rel="noreferrer" download className="flex-1" data-testid="gallery-download">
                        <Button variant="outline" className="w-full border-zinc-700 text-white hover:bg-zinc-800"><Download className="h-4 w-4 mr-1.5" /> Download</Button>
                      </a>
                      <Button variant="ghost" className="text-zinc-400 hover:text-[#FF3B30]" onClick={() => remove(active.id)} data-testid="gallery-delete">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
